import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, X } from 'lucide-react';
import { Button, Dialog } from '@/design-system';
import {
  managerConnectorService,
  type InstallerRelease,
  type InstallerVariant,
} from './api/connector-gateways.service';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Default 'farmafacil'. Único adapter ativo hoje. */
  adapter?: string;
}

/**
 * Sprint Homolog: modal "Releases disponíveis" — staff lista as últimas
 * N releases do MSI/Server bundle e baixa via link autenticado (302 →
 * URL S3 assinada do GitHub).
 */
export function InstallerReleasesModal({
  open,
  onClose,
  adapter = 'farmafacil',
}: Props) {
  const [includePrerelease, setIncludePrerelease] = useState(true);
  const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

  const query = useQuery({
    queryKey: ['installer-releases', adapter, includePrerelease],
    queryFn: () => managerConnectorService.listInstallerReleases(includePrerelease, 20),
    enabled: open,
  });

  function downloadLink(variant: InstallerVariant): string {
    const params = new URLSearchParams({ variant });
    if (includePrerelease) params.set('include_prerelease', 'true');
    return `${apiBase}/api/manager/connector/installer/${adapter}/latest?${params}`;
  }

  return (
    <Dialog isOpen={open} onClose={onClose} title="Releases disponíveis" footer={(
      <div className="flex justify-end">
        <Button size="sm" variant="secondary" onClick={onClose}>
          <X size={14} className="mr-1.5" aria-hidden="true" />
          Fechar
        </Button>
      </div>
    )}>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-gray-600">
            Baixe instaladores Windows do Orya Connector. Última versão
            recomendada aparece no topo.
          </p>
          <label className="inline-flex shrink-0 items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={includePrerelease}
              onChange={(e) => setIncludePrerelease(e.target.checked)}
            />
            <span>Incluir prereleases (rc/beta)</span>
          </label>
        </div>

        {/* Botões da última versão (atalho) */}
        <div className="flex flex-wrap gap-2 rounded-lg border border-violet-200 bg-violet-50 p-3">
          <span className="text-xs font-medium text-violet-900">
            Última versão:
          </span>
          <a
            href={downloadLink('shell')}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700"
          >
            <Download size={12} aria-hidden="true" />
            Desktop (MSI)
          </a>
          <a
            href={downloadLink('server')}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md bg-violet-200 px-3 py-1.5 text-xs font-medium text-violet-900 hover:bg-violet-300"
          >
            <Download size={12} aria-hidden="true" />
            Server (MSI)
          </a>
          <a
            href={downloadLink('server-zip')}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md bg-violet-200 px-3 py-1.5 text-xs font-medium text-violet-900 hover:bg-violet-300"
          >
            <Download size={12} aria-hidden="true" />
            Server Core (ZIP)
          </a>
        </div>

        {query.isLoading ? (
          <p className="text-sm text-gray-500">Carregando…</p>
        ) : query.isError ? (
          <p className="text-sm text-red-600">Falha ao carregar releases.</p>
        ) : !query.data?.length ? (
          <p className="text-sm text-gray-500">Nenhuma release encontrada.</p>
        ) : (
          <div className="max-h-[420px] space-y-1.5 overflow-y-auto">
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
              Histórico
            </p>
            {query.data.map((r) => (
              <ReleaseRow key={r.tag} release={r} apiBase={apiBase} adapter={adapter} />
            ))}
          </div>
        )}

      </div>
    </Dialog>
  );
}

function ReleaseRow({
  release,
  apiBase,
  adapter,
}: {
  release: InstallerRelease;
  apiBase: string;
  adapter: string;
}) {
  const date = new Date(release.published_at).toLocaleDateString('pt-BR');
  const links: Array<{ variant: InstallerVariant; label: string; className: string }> = [
    {
      variant: 'shell',
      label: 'MSI',
      className: 'border-violet-300 text-violet-700 hover:bg-violet-50',
    },
    {
      variant: 'server',
      label: 'Server',
      className: 'border-gray-300 text-gray-700 hover:bg-gray-50',
    },
    {
      variant: 'server-zip',
      label: 'Server Core',
      className: 'border-gray-300 text-gray-700 hover:bg-gray-50',
    },
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-mono text-sm font-medium text-gray-900">
              {release.tag}
            </span>
            {release.prerelease && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                prerelease
              </span>
            )}
            <span className="text-xs text-gray-500">{date}</span>
          </div>
        </div>
        <div className="flex shrink-0 gap-1.5">
          {links.map(({ variant, label, className }) => {
            const asset = release.assets.find((a) => a.variant === variant);
            if (!asset) return null;
            return (
              <a
                key={variant}
                href={`${apiBase}/api/manager/connector/installer/${adapter}/latest?variant=${variant}${
                  release.prerelease ? '&include_prerelease=true' : ''
                }`}
                target="_blank"
                rel="noopener noreferrer"
                title={`${asset.name} (${formatSize(asset.size_bytes)})`}
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium ${className}`}
              >
                <Download size={11} aria-hidden="true" />
                {label}
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}
